/**
 * Small WCAG color utilities used to keep the banner readable regardless of
 * the tenant-configured palette. Pure functions, exported for unit tests.
 *
 * Inputs are expected to have passed sanitizeCssValue() already; anything we
 * cannot parse (named colors, gradients, var()) degrades gracefully: the
 * caller falls back to its previous fixed behavior rather than crashing.
 */

export type Rgb = [number, number, number];

/** Parse #rgb, #rrggbb, #rrggbbaa, rgb() and rgba() strings. Alpha is ignored. */
export function parseColor(input: string): Rgb | null {
  const value = String(input ?? "").trim();

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value);
  if (hex) {
    let h = hex[1];
    if (h.length === 3 || h.length === 4) {
      h = h.split("").map((c) => c + c).join("");
    }
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }

  const fn = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i.exec(value);
  if (fn) {
    const r = Number(fn[1]);
    const g = Number(fn[2]);
    const b = Number(fn[3]);
    if (r > 255 || g > 255 || b > 255) return null;
    return [r, g, b];
  }

  return null;
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance([r, g, b]: Rgb): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio between two colors (1..21). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Pick the more readable of two text colors for the given background.
 * The dark default is pure black: on mid-luminance brand colors (e.g. the
 * seeded #6366f1) white lands just under 4.5:1 while black clears it.
 * Unparsable backgrounds return `light` (the pre-existing hardcoded value),
 * preserving today's behavior for exotic tenant values.
 */
export function readableTextColor(bg: string, light: string = "#ffffff", dark: string = "#000000"): string {
  const bgRgb = parseColor(bg);
  if (!bgRgb) return light;
  const lightRgb = parseColor(light) ?? [255, 255, 255];
  const darkRgb = parseColor(dark) ?? [17, 24, 39];
  return contrastRatio(bgRgb, lightRgb) >= contrastRatio(bgRgb, darkRgb) ? light : dark;
}

/** Alpha-blend a foreground color over a background. */
export function blend(fg: Rgb, alpha: number, bg: Rgb): Rgb {
  return [
    Math.round(fg[0] * alpha + bg[0] * (1 - alpha)),
    Math.round(fg[1] * alpha + bg[1] * (1 - alpha)),
    Math.round(fg[2] * alpha + bg[2] * (1 - alpha)),
  ];
}

function toHex([r, g, b]: Rgb): string {
  return "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("");
}

/**
 * A "muted" variant of `text` on `background`: visually softened, but never
 * below `minRatio` contrast. Starts at `startAlpha` and steps the blend back
 * toward full strength until the ratio is met. If either color is unparsable,
 * returns `text` verbatim (solid, safe, never low-contrast).
 */
export function mutedColor(text: string, background: string, minRatio: number = 4.5, startAlpha: number = 0.7): string {
  const textRgb = parseColor(text);
  const bgRgb = parseColor(background);
  if (!textRgb || !bgRgb) return String(text ?? "");
  for (let alpha = startAlpha; alpha < 1; alpha += 0.05) {
    const blended = blend(textRgb, alpha, bgRgb);
    if (contrastRatio(blended, bgRgb) >= minRatio) return toHex(blended);
  }
  return toHex(textRgb);
}

/**
 * `fg` itself when it already reaches `minRatio` against `bg`, otherwise `fg`
 * blended toward black or white (whichever `bg` contrasts with more) just far
 * enough to reach it. Keeps a tenant's brand hue recognisable while making
 * links (4.5:1, WCAG 1.4.3) and focus rings (3:1, WCAG 1.4.11) legible on the
 * tenant's own background. Unparsable input is returned verbatim.
 */
export function accessibleColor(fg: string, bg: string, minRatio: number): string {
  const fgRgb = parseColor(fg);
  const bgRgb = parseColor(bg);
  if (!fgRgb || !bgRgb) return String(fg ?? "");
  if (contrastRatio(fgRgb, bgRgb) >= minRatio) return String(fg);
  const black: Rgb = [0, 0, 0];
  const white: Rgb = [255, 255, 255];
  const target = contrastRatio(black, bgRgb) >= contrastRatio(white, bgRgb) ? black : white;
  for (let step = 1; step <= 20; step++) {
    const candidate = blend(target, step / 20, fgRgb);
    if (contrastRatio(candidate, bgRgb) >= minRatio) return toHex(candidate);
  }
  return toHex(target);
}
